import React, { useState, useEffect } from "react";
import { Button, ImageWithFallback } from "../components/Utilities";
import imgTop from "../assets/imgTop.png";
import imgBottom from "../assets/imgBottom.png";
import rc_yes from "../assets/rc-yes.svg";
import rc_no from "../assets/rc-no.svg";
import clock from "../assets/clock.svg";
import locCard from "../assets/locCard.svg";
import "../DealerHome.css";
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination, Autoplay } from 'swiper/modules';

export const LiveTimer = ({ startDateStr, endDateStr }) => {
  const parseDate = (dateStr) => {
    if (!dateStr || typeof dateStr !== "string") return null;

    // Normalize: "2025-10-29T19:14" → "2025-10-29T19:14:00Z"
    let normalized = dateStr.trim();

    // Replace space with T (if any)
    normalized = normalized.replace(" ", "T");

    // Add seconds and Z if missing
    if (!normalized.includes(":")) return null;
    if (!normalized.includes("Z") && !normalized.includes("+") && !normalized.includes("-")) {
      normalized = normalized.replace(/(\d{4}-\d{2}-\d{2}T\d{2}:\d{2})$/, "$1:00Z");
    }

    const date = new Date(normalized);
    return isNaN(date.getTime()) ? null : date;
  };

  const calculateTimeLeft = () => {
    if (!startDateStr || !endDateStr) {
      return "N/A";
    }

    const startDate = parseDate(startDateStr);
    const endDate = parseDate(endDateStr);

    if (!startDate || !endDate) {
      console.warn("Invalid date parsing:", { startDateStr, endDateStr });
      return "N/A";
    }

    const now = new Date();

    // 1. Before start → Not Started
    if (now < startDate) {
      return "Not Started";
    }

    // 2. After end → Expired
    if (now >= endDate) {
      return "Expired";
    }

    // 3. Active → Show countdown
    const diffSeconds = Math.floor((endDate - now) / 1000);

    const days = Math.floor(diffSeconds / 86400);
    const hours = Math.floor((diffSeconds % 86400) / 3600);
    const minutes = Math.floor((diffSeconds % 3600) / 60);
    const seconds = diffSeconds % 60;

    if (days > 0) {
      return `${days}D:${hours.toString().padStart(2, "0")}H`;
    } else if (hours > 0) {
      return `${hours}H:${minutes.toString().padStart(2, "0")}M`;
    } else {
      return `${minutes}M:${seconds.toString().padStart(2, "0")}S`;
    }
  };

  const [timeLeft, setTimeLeft] = useState(calculateTimeLeft);

  useEffect(() => {
    const interval = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);

    return () => clearInterval(interval);
  }, [startDateStr, endDateStr]);

  return (
    <div className="time-badge">
      <ImageWithFallback src={clock} alt="Time badge" />
      <span className="time">{timeLeft}</span>
    </div>
  );
};

const CardLayout = ({
  car,
  btn1,
  btn2,
  isDisabled,
  btn1_label,
  btn2_label,
  showBidInfo = false,
  isRemoving,
  enableRemoveAnimation = false,
}) => {
  const [isAdding, setIsAdding] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  if (!car) return null;

  // Debug: Log car data to see isSold field
  console.log('CardLayout - Car data:', {
    id: car.id,
    isSold: car.isSold,
    isSoldType: typeof car.isSold,
    isSoldTruthy: !!car.isSold
  });

  // Test: Force show SOLD overlay for testing
  const shouldShowSold = car.isSold || car.id === 'BM7VNKLHku46PXFsNk40';
  console.log('Should show SOLD overlay:', shouldShowSold);

  const handleAddClick = (event) => {
    event.stopPropagation();
    if (isDisabled || isAdding) return;
    setIsAdding(true);
    setIsAnimating(true);
    btn1(car, () => {
      // Animation completion callback
      setTimeout(() => {
        setIsAnimating(false);
        setIsAdding(false)
      }, 300); // Match animation duration
    });
  };

  const buttonDisabled = isDisabled || isAdding;
  return (
    <div onClick={() => btn2(car)} className={`car-card ${enableRemoveAnimation && (isAnimating || isRemoving) ? "removing" : ""} ${car.isSold ? "sold" : ""}`}>
      {/* SOLD Overlay */}
      {car.isSold && (
        <div className="sold-overlay">
          <div className="sold-badge">
            <span className="sold-text">SOLD</span>
          </div>
        </div>
      )}
      
      <div className="car-image-wrapper">
        <img src={imgTop} className="imgTop" alt="" />
        <img src={imgBottom} className="imgBottom" alt="" />
          {car.evaluationImages && car.evaluationImages.length > 0 ? (
        <Swiper
          modules={[Pagination, Autoplay]}
          spaceBetween={0}
          slidesPerView={1}
          loop={true}
          autoplay={{
            delay: 2500,
            disableOnInteraction: false,
          }}
          pagination={{ clickable: true }}
          // navigation={car.evaluationImages.length > 1}
          className="car-image-carousel"
          allowTouchMove={true}
        >
          {car.evaluationImages.map((img, idx) => (
            <SwiperSlide key={img.id || idx}>
              <div className="carousel-image-container">
                <ImageWithFallback
                  src={img.url}
                  alt={`${car.brand} ${car.model} - ${img.angle || idx + 1}`}
                  className="car-image"
                />
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      ) : (
        <ImageWithFallback
          src={
            car.car_image_urls && car.car_image_urls.length > 0
              ? car.car_image_urls[0].url || car.car_image_urls[0]
              : "https://via.placeholder.com/150"
          }
          alt="Steering Wheel"
          className="car-image"
        />
      )}
        <LiveTimer startDateStr={car.startDate} endDateStr={car.endDate} />
        <div className="car-location">
          <ImageWithFallback src={locCard} alt="Location" />
          <p className="car-location-name">{car.location}</p>
        </div>
      </div>

      <div className="car-details">
        <div className="car-info">
          <h3 className="car-title">
            {car.year} {car.brand} {car.model}
          </h3>
          <div className="car-spec-container">
            <div className="car-specs">
              <span className="distance">
                {car.rc ? (
                  <img src={rc_yes} alt="" className="rc" />
                ) : (
                  <img src={rc_no} alt="" className="rc" />
                )}
                {car.km.toLocaleString()} kms
              </span>
              <span className="car-fuel">
                {" • "}
                {car.fuel} • {car.transmission}
              </span>
            </div>
            <div className="car-tags">
              {car.rc ? (
                <img src={rc_yes} alt="RC Yes" className="rc" />
              ) : (
                <img src={rc_no} alt="RC No" className="rc" />
              )}
              <span className="rto-tag">{car.rto}</span>
              <span className="owner-tag">{car.owner}</span>
            </div>
            <div className="phone-car-location">
              <ImageWithFallback src={locCard} alt="Location" />
              <p className="car-location-name">{car.location}</p>
            </div>

            {showBidInfo && (
              <div className="car-bid-info">
                <div className="car-price">
                  ₹ {car.price.toLocaleString()}
                </div>
                <div className="bids-status">
                  Bids Placed: {car.bidsPlaced || "2/5"}
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="car-actions">
          <Button
            className="btn-full"
            variant="primary"
            onClick={handleAddClick}
            disabled={buttonDisabled}
            title={buttonDisabled ? "Adding…" : ""}
          >
            {btn1_label}
          </Button>
          <Button
            variant="outline"
            className="btn-full"
            onClick={event => { event.stopPropagation(); btn2(car); }}
          >
            {btn2_label}
          </Button>
        </div>
      </div>
      <Button
        className="phone-btn-full"
        onClick={handleAddClick}
        disabled={buttonDisabled}
        title={buttonDisabled ? "Adding…" : ""}
      >
        {btn1_label}
      </Button>
    </div>
  );
};

export default CardLayout;